import { staffAuth, clubSession, memberSession } from '../auth/instance';
import { getImpersonationToken, reportImpersonationRefused } from './impersonationToken';

import type { ClubSession } from '../auth/clubSession';
import { CLUB_TOKEN_PREFIX, MEMBER_TOKEN_PREFIX } from './tokenFamilies';
import { ENTRA_REQUIRED, reportEntraRequired } from './entraRequiredSignal';
import {
  REAUTHENTICATION_REQUIRED,
  reportReauthenticationRequired,
} from './reauthenticationSignal';
import {
  customerContext,
  requestCredential,
  type CustomerContext,
  type RequestCredential,
} from './customerContext';
import { apiBaseUrl } from './baseUrl';
import { ScopeTransitionError, scopeTransition } from './scopeTransition';
import { reportServerAnswered, reportServerSilent } from './serverReachability';

// ─────────────────────────────────────────────────────────────────────
// API TRANSPORT — one place that talks to @starpoint/api.
//
// Every call to the API goes through `apiFetch`, so "does this request carry a
// bearer token?" has exactly one answer in the codebase instead of twenty-one.
// Before this existed each module called `fetch(apiBase() + path)` directly,
// which is fine until the day a token is required — then it is twenty-one
// places to remember, and the one that gets forgotten fails in production.
//
// WHAT THIS SLICE DOES AND DOES NOT DO. The token is attached whenever the app
// is in API mode AND a staff member is signed in. BOTH HOLD NOW: the cutover
// happened (#186), `VITE_API_URL` is set in the deployed environments, and
// this is the live transport rather than the readiness it was written as.
// See docs/architecture/SECURITY.md §1.
// ─────────────────────────────────────────────────────────────────────

// `apiBaseUrl` / `isApiMode` moved to ./baseUrl so the club session can read
// them without importing the transport that attaches its token. Re-exported
// here because every existing caller imports them from this module.
export { apiBaseUrl, isApiMode } from './baseUrl';

/**
 * How a client says which organization a request concerns.
 *
 * Mirrors TENANT_HEADER in apps/api/src/auth/tenantContext.ts. The two must
 * agree exactly: a single character of drift leaves every request unscoped and
 * every tenant route answering 400, which is what this slice exists to fix.
 */
export const TENANT_HEADER = 'X-Starpoint-Tenant';

/** Injectable seams. Defaults are the real thing; tests pass fakes. */
export interface ApiTransportDeps {
  baseUrl: string;
  getToken: () => Promise<string | null>;
  /**
   * May the browser's AMBIENT cookie authenticate this request? (#839 re-review)
   *
   * Absent means yes, which is every existing caller and every surface whose
   * cookie IS its credential — a reload holds nothing else. It answers NO for
   * exactly one case, and that case was a silent wrong-actor bug: the staff
   * surface claiming an Entra identity whose token could not be obtained. See
   * `requestCredential`. Sending no bearer AND no cookie is what makes the
   * server answer 401 instead of succeeding as whoever the cookie names.
   */
  ambientAllowed?: () => Promise<boolean>;
  /** The organization to scope this request to, or null for none. */
  getTenantId?: () => string | null;
  fetchImpl: typeof fetch;
  /** How long the operation may take before it is abandoned. See `API_TIMEOUT_MS`. */
  timeoutMs?: number;
  /**
   * The API refused the token we sent. Called only when WE attached one, so an
   * endpoint that answers 401 to an anonymous request cannot sign anybody out.
   *
   * AWAITED, and its answer may change one thing (#783): `'cookie-answers'`
   * says the session survived on the browser's cookie — the bearer was
   * superseded by a rotation, not revoked with the session — so a request
   * that is safe to repeat is sent ONCE more, without the bearer. See
   * `apiFetch`.
   */
  onUnauthorized?: (token: string) => void | Promise<UnauthorizedOutcome>;
}

/** What `onUnauthorized` decided. Only `'cookie-answers'` changes anything. */
export type UnauthorizedOutcome = 'cookie-answers' | undefined;

/**
 * What a refused CLUB bearer does to the session that sent it (#783).
 *
 * Exported so a test can drive several tabs through the production wiring;
 * `defaultDeps` is the only production caller.
 *
 * ASK BEFORE ENDING. The session checks whether the browser's cookie still
 * vouches for the same account — a tab that slept through a rotation in
 * another tab holds a revoked bearer and a live session — and only a
 * `'final'` verdict reaches `invalidate`, which is local and sends nothing.
 */
export async function handleClubRefusal(
  session: Pick<ClubSession, 'checkRefusal' | 'invalidate'>,
  token: string,
): Promise<UnauthorizedOutcome> {
  const verdict = await session.checkRefusal(token);
  if (verdict === 'superseded') return 'cookie-answers';
  if (verdict === 'final') session.invalidate(token);
  return undefined;
}

function defaultDeps(): ApiTransportDeps {
  // ── ONE SELECTION PER REQUEST (#764 review) ────────────────────────
  //
  // `getToken()` is AWAITED — MSAL can take seconds — and `getTenantId()` ran
  // afterwards, each resolving the context independently. A surface or scope
  // switch in between paired ACTOR A'S BEARER WITH TENANT B'S ADDRESS, which
  // the server would answer perfectly correctly for the wrong person.
  //
  // `defaultDeps()` is evaluated per `apiFetch` call, so a context captured
  // here is this request's and cannot drift between the two fields. Resolved
  // lazily, because a request that asks for neither should not read it at all.
  let captured: CustomerContext | null = null;
  const context = () => (captured ??= customerContext());

  // ── ONE CREDENTIAL DECISION PER REQUEST (#839 re-review) ───────────
  //
  // Memoised like the context above, and for a sharper reason: the two fields
  // below are two views of ONE answer, and resolving them separately would
  // call `getApiToken()` twice — a second MSAL round trip that can disagree
  // with the first. A request whose header says «no bearer» while its cookie
  // policy says «an Entra identity failed» is exactly the pair this exists to
  // keep consistent.
  let credential: Promise<RequestCredential> | null = null;
  const resolved = () =>
    (credential ??= requestCredential(context(), {
      // WHICH Entra identity the panel renders — not «a token came back»,
      // which is the thing that failed, and not merely «somebody», which
      // cannot tell a sign-out from a switch to another staff account.
      //
      // The `oid` where MSAL gave one: the same immutable id the API maps on,
      // so «still the same identity» means what the server means by it.
      identity: () => {
        const state = staffAuth.getState();
        if (state.status !== 'signed-in') return null;
        return state.identity.subject ?? state.identity.email ?? 'signed-in';
      },
      token: () => staffAuth.getApiToken(),
    }));

  return {
    baseUrl: apiBaseUrl(),
    // ── THE SURFACE DECIDES, NOT THE INVENTORY (#750 M2b-2) ────────
    //
    // This used to read «whichever token exists, in a fixed order», justified
    // by a premise that is being removed:
    //
    //     «A browser is one of these, not several: the portal and the member
    //      app are different surfaces of the same bundle and are never signed
    //      in at once.»
    //
    // A browser already holds both through the dev-persona route, and after
    // M2b-3 one account session serves both surfaces. Once two credentials can
    // be live, a fixed order is not a rule — it is a race between two sessions
    // for the same header, and the club token won it even for member requests.
    //
    // `customerContext()` answers from the SURFACE the app is rendering, which
    // is the thing that actually knows. The staff token is NOT a fallback
    // behind it — see `requestCredential`: it is reached only when no customer surface
    // is selected, because a cookie-restored member holds no in-memory token
    // and would otherwise have sent an Entra bearer to `/member/*`.
    getToken: async () => {
      const c = await resolved();
      return c.kind === 'bearer' ? c.token : null;
    },
    ambientAllowed: async () => (await resolved()).kind !== 'none',
    // AND THE ORGANIZATION COMES FROM THE SAME ANSWER. It used to be
    // `clubSession.tenantId()` for every request including the member app's —
    // the ADMINISTRATIVE context — so with both sessions live a member request
    // addressed whichever club an administrator had selected. Invisible only
    // because `/member/*` takes its tenant from the session and refuses an
    // `sps_` token outright, and both of those change in M2b-3 (#763 review).
    //
    // The impersonation context still wins on the club surface (RBAC-3); it is
    // inside `customerContext` rather than layered on top of it, so there is
    // one place that decides and not two.
    getTenantId: () => context().tenantId,
    fetchImpl: (input, init) => fetch(input, init),
    // ONLY THE SESSION THAT SUPPLIED THE TOKEN IS ENDED (#393).
    //
    // This used to tell both, reasoning that `invalidate()` is a no-op on a
    // session that does not exist. That holds only while a browser has exactly
    // one — and the member app broke it: a club-only route (`/kv`) answered
    // 401 to a valid `spm_` token and the member was signed out mid-load.
    //
    // Routed by PREFIX, the same discriminator the API itself uses, so client
    // and server agree on what a token is. An unrecognised prefix ends
    // nothing: staff tokens are MSAL's to manage, and guessing would sign
    // somebody out on a token this code does not own.
    //
    // AND THE TOKEN IS PASSED ON, not just used to route (#750 M2b-1). A
    // step-up REVOKES the previous bearer, so a request already in flight
    // comes back 401 — and ending the session on it would undo the very
    // refresh that just happened. `clubSession.invalidate(forToken)` ignores a
    // refusal that concerns a credential it has already replaced.
    //
    // AND A CLUB REFUSAL IS CHECKED BEFORE IT ENDS ANYTHING (#783) — see
    // `handleClubRefusal`. The member session is not: it has no rotation, and
    // widening this to it is a separate decision (#773).
    onUnauthorized: async (token: string) => {
      if (token.startsWith(CLUB_TOKEN_PREFIX)) return handleClubRefusal(clubSession, token);
      if (token.startsWith(MEMBER_TOKEN_PREFIX)) memberSession.invalidate();
      return undefined;
    },
  };
}

/**
 * How long a request may take before the browser stops waiting (#400).
 *
 * ── A PROMISE THAT NEVER SETTLES IS NOT AN ERROR ───────────────────
 *
 * `fetch` has no default timeout, and `staffAuth.getApiToken()` goes through
 * MSAL, where a silent acquisition that falls back to an interactive flow can
 * also fail to settle. Neither rejects, so every `catch` in the codebase is
 * beside the point: the `finally` that clears a loading flag never runs.
 *
 * That is what `/sa` looked like after a deploy — the access button read
 * «Henter…» for ever, with no error, no retry and nothing to do but reload.
 * The cause was not a bug in the gate. Every deploy restarts the App Service,
 * so the first visitor afterwards pays for container spin-up plus the Prisma
 * connection: the probe took 25s and then answered in 0.1s. A predictable
 * consequence of the deployment model, not a rare race.
 *
 * Longer than a cold start is comfortable, because the honest answer here is
 * «try again in a moment» and a retry after a warm-up succeeds immediately.
 */
export const API_TIMEOUT_MS = 20_000;

/** What a caller sees when the server never answered. */
function timedOut(path: string): Error {
  const message = `apiFetch("${path}") timed out`;
  if (typeof DOMException === 'function') return new DOMException(message, 'TimeoutError');
  const err = new Error(message);
  err.name = 'TimeoutError';
  return err;
}

/**
 * Is abandoning this request SAFE?
 *
 * ── WHY THE BOUND IS NOT APPLIED TO EVERYTHING ─────────────────────
 *
 * Aborting does not undo a write. A `POST` that timed out may have committed
 * on the server, and the caller cannot tell — so a blanket timeout here would
 * turn «slow» into «unknown», and «unknown» is the state from which a second
 * booking, a second registration or a doubled rating gets created by an
 * operator doing the obvious thing.
 *
 * A read has no such outcome: nothing changed, and asking again is free. So
 * the response is bounded for reads, and a write that CAN be bounded does it
 * at its own endpoint, where the exactly-once story is known —
 * `tournamentResults.ts` is the worked example, and it says the same thing
 * from the other side.
 *
 * THE TOKEN IS BOUNDED EITHER WAY, whatever the method: a request whose token
 * never arrived was never sent, so there is no outcome to be uncertain about.
 * That is the half that hangs `/sa`.
 */
function safeToAbandon(init: RequestInit | undefined): boolean {
  const method = (init?.method ?? 'GET').toUpperCase();
  return method === 'GET' || method === 'HEAD';
}

// ── WRITES THIS PAGE HAS SENT (#1106) ─────────────────────────────────
//
// A background re-read (the member app's focus refresh) must not replace the
// screen with an answer that may PREDATE one of this page's own writes: a read
// that left before a booking was committed, and landed after the booking was
// added locally, would make the booking vanish until the next refresh. Counted
// here because this is the one function every API write passes through, so a
// write added to a new screen is covered without anybody remembering to say so.
let writesStarted = 0;
let writesInFlight = 0;

/**
 * A marker for «no write of this page's has started or is still running since
 * now», or null when one is running already.
 *
 * Take one before a background read and compare it with `writeMarker()` again
 * when the answer arrives: equal (and not null) means the answer cannot predate
 * a write of ours. Null at either end means it might, and the answer should be
 * dropped rather than adopted.
 */
export function writeMarker(): number | null {
  return writesInFlight === 0 ? writesStarted : null;
}

/** A bound on one operation: `signal` for the fetch, `bound()` for a promise. */
interface Deadline {
  signal: AbortSignal | null;
  bound<T>(promise: Promise<T>): Promise<T>;
  expired: () => boolean;
  clear: () => void;
}

/** No bound at all — a non-positive timeout, or a runtime without AbortController. */
const UNBOUNDED: Deadline = {
  signal: null,
  bound: (promise) => promise,
  expired: () => false,
  clear: () => {},
};

function startDeadline(ms: number, path: string): Deadline {
  if (!(ms > 0) || typeof AbortController === 'undefined') return UNBOUNDED;
  const controller = new AbortController();
  let expired = false;
  const timer = setTimeout(() => {
    expired = true;
    controller.abort(timedOut(path));
  }, ms);
  // Rejects when the deadline passes, so a promise with no signal of its own —
  // the token acquisition — can still be raced against it.
  const expiry = new Promise<never>((_resolve, reject) => {
    controller.signal.addEventListener('abort', () => reject(timedOut(path)), { once: true });
  });
  // The race below attaches a handler, but only once `bound` has been called.
  // Without this, a deadline that fires before then is an unhandled rejection.
  void expiry.catch(() => {});
  return {
    signal: controller.signal,
    bound: (promise) => Promise.race([promise, expiry]),
    expired: () => expired,
    clear: () => clearTimeout(timer),
  };
}

/**
 * Build the outgoing headers.
 *
 * Pure and exported so the one rule that matters is testable on its own: the
 * Authorization header appears if and only if a token was supplied. A caller's
 * own Authorization header is never overwritten — nothing sets one today, and
 * silently replacing it would be a surprising thing for this helper to do.
 */
/** Its own header, never folded into Authorization — see below. */
export const IMPERSONATION_HEADER = 'X-StarPoint-Impersonation';

export function withAuthHeader(
  init: RequestInit | undefined,
  token: string | null,
  tenantId: string | null = null,
): Headers {
  const headers = new Headers(init?.headers);
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // WHICH ORGANIZATION this request concerns (#186). It IDENTIFIES a scope and
  // grants nothing — the server resolves it against the caller's membership and
  // refuses what they do not hold, so a client that edited it gets a 403 rather
  // than someone else's data.
  //
  // Absent when no scope is selected, so the server answers `tenant_required`
  // instead of being handed a guess. A caller that set the header itself is
  // left alone: `/tenants/:id/…` addresses a scope as a resource.
  if (tenantId && !headers.has(TENANT_HEADER)) {
    headers.set(TENANT_HEADER, tenantId);
  }
  // THE SECOND CONTEXT (RBAC-3), when one is active. It never replaces the
  // Authorization above: the request stays authenticated as the real StarPoint
  // employee, and this says which organization they have explicitly started
  // operating. The server refuses it outright if the two disagree.
  const impersonation = getImpersonationToken();
  if (impersonation && !headers.has(IMPERSONATION_HEADER)) {
    headers.set(IMPERSONATION_HEADER, impersonation);
  }
  return headers;
}

/**
 * Call the API. `path` is absolute-from-root ('/players', '/kv/foo').
 *
 * Throws when not in API mode: reaching this function without a base URL means
 * a caller took the API branch in a build that has no API, which is a bug
 * worth surfacing rather than a request worth sending somewhere relative.
 */
export async function apiFetch(
  path: string,
  init?: RequestInit,
  deps: ApiTransportDeps = defaultDeps(),
): Promise<Response> {
  const { baseUrl, getToken, ambientAllowed, fetchImpl } = deps;
  if (!baseUrl) {
    throw new Error(`apiFetch("${path}") called while the app is not in API mode`);
  }

  // A signed-out staff member still gets a request, just an unauthenticated
  // one — the API answers 401 and the caller handles it. Guessing here would
  // move an authorization decision into the browser.
  //
  // The token is passed straight to the header and never stored, never
  // returned, and never logged (see lib/auth/staffAuth.ts).
  // ONE DEADLINE FOR THE WHOLE OPERATION (#400). It always covers the token
  // acquisition, because a request whose token never arrived was never sent;
  // it covers the RESPONSE only when abandoning it is safe. See
  // `safeToAbandon`.
  const isWrite = !safeToAbandon(init);
  // ── NOT BETWEEN TWO ORGANIZATIONS (AUTHORIZATION.md «Still to build» 3) ──
  //
  // A write made while the portal is moving from one scope to another is the
  // tail of an operation begun under the previous one (lib/api/scopeTransition.ts),
  // and the organization resolved below would be the NEW one. Refused before
  // the token is even asked for, so nothing is sent and the write marker is
  // untouched: a request that never left cannot be a write a read must wait for.
  //
  // THE ADDRESS IS READ FIRST, beside the check and before any await, so a
  // request that passed is addressed to the organization it was made under
  // even if the scope moves while the token is being fetched (review L2) —
  // and resolved ONCE, so a replay below addresses the same organization the
  // refused request did rather than whatever is selected by then.
  //
  // ONLY A WRITE THAT CARRIES AN ADDRESS IS REFUSED. A platform route and the
  // account's own `/me` name no organization, so there is no wrong one for
  // them to land in — and leaving an organization for «Min StarPoint» opens a
  // window that only the next club's load closes, which the account's own
  // writes (ending an impersonation, saving a profile) must not wait for.
  const tenantId = deps.getTenantId?.() ?? null;
  if (isWrite && tenantId !== null) {
    const transition = scopeTransition();
    if (transition) throw new ScopeTransitionError(transition);
  }
  const deadline = startDeadline(deps.timeoutMs ?? API_TIMEOUT_MS, path);
  if (isWrite) {
    writesStarted += 1;
    writesInFlight += 1;
  }
  try {
    let token: string | null = null;
    try {
      token = await deadline.bound(getToken());
    } catch (err) {
      // A DEADLINE IS NOT A TOKEN PROBLEM. Swallowing it here would send an
      // unauthenticated request, and the 401 that came back would be reported
      // as «the server did not accept your sign-in» — a wrong answer that
      // sends somebody off to re-authenticate over what was a slow start.
      if (deadline.expired()) throw err;
      // Everything else is reported through the auth state, not by failing
      // the request here.
      token = null;
    }

    // ── MAY THE COOKIE ANSWER FOR THIS ONE? (#839 re-review) ────────
    //
    // Asked only after the bearer, because the answer depends on why there is
    // none. A failure here is read as NO: the whole point of this branch is
    // that «we could not establish the credential» must not become «the
    // ambient one will do».
    let ambient = true;
    if (ambientAllowed) {
      try {
        ambient = await deadline.bound(ambientAllowed());
      } catch (err) {
        if (deadline.expired()) throw err;
        ambient = false;
      }
    }

    const send = async (bearer: string | null): Promise<Response> => {
      const request = fetchImpl(baseUrl + path, {
        ...init,
        // THE SESSION COOKIE TRAVELS (#569). Without this a browser attaches no
        // cookie to a cross-ORIGIN request — which every portal call is, the API
        // being a sibling host of the same site — so the session would survive a
        // reload in the browser's jar and be absent from every request made
        // afterwards. The server's CORS is credentialed for the same reason.
        //
        // The bearer header still takes precedence server-side wherever the
        // in-memory token exists; the cookie is what answers after a reload.
        //
        // `'omit'` IS NOT A TIMEOUT OR A TIDY-UP — it is the fail-closed branch
        // of `requestCredential`, and the only thing that stops a staff request
        // whose Entra token could not be obtained from succeeding as whatever
        // customer session the browser happens to hold. The server consults the
        // cookie precisely when no `Authorization` is present, so suppressing
        // the header alone would not have been enough.
        credentials: ambient ? 'include' : 'omit',
        headers: withAuthHeader(init, bearer, tenantId),
        // A CALLER'S OWN SIGNAL IS NEVER REPLACED — it is how a component
        // cancels on unmount, and overwriting it would leave that cancellation
        // silently inert.
        ...(deadline.signal && !init?.signal && safeToAbandon(init)
          ? { signal: deadline.signal }
          : {}),
      });

      // RACED AS WELL AS SIGNALLED, for reads. The signal is what closes the
      // socket, and a real `fetch` rejects on it — but the bound must not DEPEND
      // on the implementation honouring it, because the failure mode when it
      // does not is the exact one this exists to remove: a promise that never
      // settles, and a `finally` that never runs.
      const answer = safeToAbandon(init) ? await deadline.bound(request) : await request;

      // THE SERVER ANSWERED (#537). Reported here rather than by each caller,
      // because this is the one line every API call in the app passes through.
      // A 4xx counts as answered: it is a refusal, not an outage, and reporting
      // it as one would tell somebody to wait for a recovery that already
      // happened.
      reportServerAnswered(answer.status);
      return answer;
    };

    let res = await send(token);

    // A 401 ON A REQUEST WE AUTHENTICATED means the session is over — expired,
    // revoked, or the account disabled. The session is dropped here, once, rather
    // than by each of the twenty-odd callers, and the gate returns to the login
    // screen because it is subscribed to that.
    //
    // With one exception, for the club session (#783): its bearer can be
    // revoked by a rotation in ANOTHER tab while the session lives on in the
    // shared cookie, so the session is asked before it is ended. See
    // `handleClubRefusal`.
    //
    // The `token` guard is the load-bearing part: a 401 on a request that carried
    // NOTHING says only that the endpoint needs authentication, which is true of
    // every call made before anyone signs in. Signing the user out on those would
    // make the portal unusable the moment a single anonymous read happened.
    //
    // WHICH session ended is decided by the token PREFIX (#393). Telling both
    // was defended as harmless because `invalidate()` is a no-op on a session
    // that does not exist — true, and beside the point the day BOTH could not
    // exist at once is the day it stops being true. A club-only route reached
    // from the member surface answers 401, and the member session it killed
    // was perfectly valid.
    //
    // A REFUSED CONTEXT IS NOT A REFUSED IDENTITY (#438 review). The API answers
    // 401 with `impersonation_expired` / `_revoked` / `_wrong_actor` /
    // `_invalid`, and none of those mean the person signed in is anybody other
    // than who they were a second ago. Ending their session would be the wrong
    // answer; leaving the context up is the one that shipped, and it left the
    // banner naming an organization the operator could no longer act in.
    //
    // Checked BEFORE the session routing and returned from, so the two cannot
    // both fire: the context is the thing that ended.
    if (res.status === 401 && (await refusedTheContext(res))) {
      reportImpersonationRefused();
      return res;
    }
    if (res.status === 401 && token) {
      const outcome = await deps.onUnauthorized?.(token);
      // ── THE SESSION SURVIVED ON THE COOKIE: ASK AGAIN, ONCE (#783) ──
      //
      // The bearer was superseded by a rotation this tab never heard about,
      // and the session itself is live — so the refusal says nothing about
      // the request, only about the credential it carried. A READ is sent
      // again without the bearer, which lets the cookie answer, and its
      // answer is the caller's.
      //
      // ONCE, and structurally so: the repeat carries no bearer, and this
      // branch is reached only for a request that carried one. A repeat
      // refused in turn is returned as it is.
      //
      // NEVER A WRITE. A 401 from `requireAuth` is answered before any
      // handler runs, so a repeat would very likely be harmless — but
      // «very likely» on a booking or a result is the uncertainty
      // `safeToAbandon` exists to keep out of this file. The write's caller
      // gets the 401, the session is still signed in, and the next attempt
      // goes out on the cookie.
      //
      // And only where the cookie was allowed to travel in the first place:
      // a request that `requestCredential` sent with `'omit'` must not have
      // its credential policy widened by a retry.
      if (outcome === 'cookie-answers' && ambient && safeToAbandon(init)) {
        res = await send(null);
        if (res.status === 401 && (await refusedTheContext(res))) {
          reportImpersonationRefused();
          return res;
        }
      }
    }

    // ── ORGANIZATION AUTHORITY NEEDS A RECENT AUTHENTICATION (#750) ──
    //
    // 403 `reauthentication_required`. Reported here for the reason every
    // other cross-cutting answer is: this is the one line every API call
    // passes through, and the alternative is each of the twenty-odd route
    // clients recognising a refusal that has nothing to do with what it asked
    // for.
    //
    // NOT A SESSION ENDING. The person is who they were a second ago and
    // their session is live — what has lapsed is the twelve-hour window on
    // ADMINISTRATIVE authority. Signing them out would be both wrong and
    // worse than the prompt.
    //
    // The response is returned UNTOUCHED either way: the caller still handles
    // its own failure, and this observes the refusal on the way past.
    //
    // ── AND PLATFORM AUTHORITY NEEDS A MICROSOFT SIGN-IN (#1002, #1030) ──
    //
    // 403 `entra_required`, read from the same clone. Reported for the
    // sentence (`entraRequiredSignal.ts`), and it ENDS AN ACTIVE CONTEXT the
    // way an `impersonation_*` refusal does: the API refuses a context
    // presented on a session Entra did not prove, so a banner still naming
    // the organization would be the operator believing they act in a club
    // they cannot reach. `reportImpersonationRefused` is a no-op when there
    // is none.
    if (res.status === 403) await reportForbidden(res, path);
    return res;
  } catch (err) {
    // NO ANSWER AT ALL — a thrown fetch, or a deadline nothing came back
    // from. This is the case a user sees as «the button does nothing», and it
    // is the only case that should say the server is unreachable.
    //
    // RE-THROWN UNCHANGED, so every caller's error handling is unaffected:
    // this observes the failure on the way past and decides nothing about it.
    reportServerSilent();
    throw err;
  } finally {
    // Always, so a completed request leaves no timer that could abort a
    // later one through a signal it happens to share.
    deadline.clear();
    if (isWrite) writesInFlight -= 1;
  }
}

/**
 * Was this 403 «your authentication is too old», or «this needs Microsoft»?
 *
 * Read from a CLONE so the caller still gets an untouched body, the same way
 * `refusedTheContext` does and for the same reason: every route client reads
 * its own error. A body rather than a header, also for that reason — a header
 * the browser must be told to EXPOSE has gone wrong three times here already.
 */
async function reportForbidden(res: Response, path: string): Promise<void> {
  if (typeof res.clone !== 'function') return;
  try {
    const body = (await res.clone().json()) as { error?: unknown; windowSeconds?: unknown };
    if (body?.error === REAUTHENTICATION_REQUIRED) {
      reportReauthenticationRequired(
        typeof body.windowSeconds === 'number' ? body.windowSeconds : null,
        path,
      );
    } else if (body?.error === ENTRA_REQUIRED) {
      // The context first, so whatever renders the sentence already sees the
      // operator back outside it.
      reportImpersonationRefused();
      reportEntraRequired();
    }
  } catch {
    // A 403 with no JSON body is an ordinary refusal — a capability the
    // account does not hold — and says nothing about freshness or doors.
  }
}

/**
 * Did this 401 refuse the CONTEXT, rather than the identity?
 *
 * Read from a CLONE, so the caller still gets an untouched body — every route
 * client reads the error itself. Deliberately not a response header: the
 * preflight has now failed to admit a header three times in this codebase, and
 * a header the browser must be told to EXPOSE is a fourth chance to get that
 * wrong. A body needs no permission.
 *
 * Matched on the `impersonation_` PREFIX rather than an enumerated list, so a
 * fifth refusal code added on the server does not need a matching edit here to
 * take effect. Erring toward clearing is the safe direction: a context the
 * server will not honour must not stay live in front of an operator.
 */
async function refusedTheContext(res: Response): Promise<boolean> {
  // No context presented, nothing to refuse — and no body to read.
  if (!getImpersonationToken()) return false;
  // A fake without `clone` cannot be inspected; production always has one.
  if (typeof res.clone !== 'function') return false;
  try {
    const body = (await res.clone().json()) as { error?: unknown };
    return typeof body?.error === 'string' && body.error.startsWith('impersonation_');
  } catch {
    // A 401 with no JSON body says nothing about the context, so it is left
    // alone rather than guessed at.
    return false;
  }
}

/** JSON convenience for the whole-collection PUTs the API clients use. */
export function jsonBody(value: unknown): RequestInit {
  return {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(value),
  };
}
